import { Button, Form, Input, Radio, Space, Switch } from "antd";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { trimRequired } from "src/common/validationRules";
import { PageSubheader } from "src/components/PageSubheader";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RefSelect } from "src/components/RefSelect/RefSelect";
import { SafeForm } from "src/components/SafeForm/SafeForm";
import { ZUser } from "src/types";
import { ZRole } from "src/types/ZRole";

import { IdBox } from "../../../../../components/IdBox";
import { RefSelectStore } from "../../../../../components/RefSelect/RefSelectStore";
import { UserFormData } from "../types";
import { userSaveData2UserFormData } from "./transform";
import * as styles from "./UserForm.module.less";

const rolesColumns = (): RtColumn<ZRole>[] => [
  {
    key: "id",
    title: "ID",
    width: 200,
    render: (row) => row.id,
  },
  {
    key: "name",
    title: t("Name"),
    width: 150,
    render: (row) => row.name,
  },
];

type PropsManagementUserForm = {
  rolesRefStore: RefSelectStore<ZRole, {}>;
  sourceData: ZUser | undefined;
  disabled: boolean;
  saving: boolean;
  extraButtons?: React.ReactNode;
  onSubmit: (data: UserFormData) => void;
  onCancel: () => void;
};

export const UserForm: React.FC<PropsManagementUserForm> = observer((props) => {
  const {
    rolesRefStore,
    sourceData,
    disabled,
    saving,
    extraButtons,
    onSubmit,
    onCancel,
  } = props;
  const [form] = Form.useForm();
  const isMetadataEditor = Form.useWatch(["metadataEditor"], form);
  const isNew = !sourceData?.id;

  const initialData = useMemo<UserFormData>(
    () =>
      sourceData
        ? userSaveData2UserFormData(sourceData)
        : {
            active: true,
          },
    [sourceData],
  );

  const columns = useMemo(() => rolesColumns(), []);

  return (
    <SafeForm
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      disabled={disabled || saving}
      initialValues={initialData}
    >
      <PageSubheader
        title={
          isNew
            ? t("New [entity]", { entity: t("User") })
            : t("Edit [entity]", { entity: t("User") })
        }
      >
        <Button onClick={onCancel}>{t("Cancel")}</Button>
        {extraButtons}
        <Button type="primary" htmlType="submit" loading={saving}>
          {isNew ? t("Create") : t("Save")}
        </Button>
      </PageSubheader>

      <div className={styles.formBox}>
        <Form.Item
          name="firstName"
          label={t("First name")}
          rules={[trimRequired(t("Field required"))]}
        >
          <Input allowClear />
        </Form.Item>

        <Form.Item
          name="lastName"
          label={t("Last name")}
          rules={[trimRequired(t("Field required"))]}
        >
          <Input allowClear />
        </Form.Item>

        <Form.Item name="middleName" label={t("Middle name")}>
          <Input allowClear />
        </Form.Item>

        <Form.Item
          name="email"
          label={t("Email")}
          rules={[
            trimRequired(t("Field required")),
            { type: "email", message: t("Invalid email") },
          ]}
        >
          <Input allowClear />
        </Form.Item>

        <Form.Item
          name="login"
          label={t("Login")}
          rules={[trimRequired(t("Field required"))]}
        >
          <Input allowClear />
        </Form.Item>

        <IdBox entity={sourceData} />

        <Form.Item name="metadataEditor" label={t("Metadata editor")}>
          <Switch />
        </Form.Item>

        <Form.Item
          name="roles"
          label={t("Roles")}
          rules={[
            {
              required: !isMetadataEditor,
              message: t("At least one role required"),
            },
          ]}
        >
          <RefSelect
            disabled={disabled || saving || isMetadataEditor}
            refSelectStore={rolesRefStore}
            columns={columns}
          />
        </Form.Item>

        <Form.Item
          name="active"
          label={t("Status")}
          tooltip={{
            title:
              "При статусе “Деактивирован” доступ пользователя к платформе блокирован. Для восстановления доступа установите статус “Активен”",
            placement: "right",
            styles: {
              root: {
                maxWidth: 465,
              },
            },
          }}
        >
          <Radio.Group>
            <Space direction="vertical" size={8}>
              <Radio value>{t("Active")}</Radio>
              <Radio value={false}>{t("Deactivated")}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </div>
    </SafeForm>
  );
});
